import type { EncryptionConfig } from '../services/webview-encryption-service';

/** Injected into WebView JS — phase 0 is valid; truthy checks on phase reject it. */
export const ORBITAL_PIE_READY_JS =
  '!!(window.PIE && window.PIE.key_id && window.PIE.phase != null)';
export const ORBITAL_PIE_MISSING_JS =
  '!window.PIE || !window.PIE.key_id || window.PIE.phase == null';

/**
 * Generate HTML for Orbital (Safetech) encryption.
 *
 * Extracted from encryption-webview.tsx so the readiness-check and encryption
 * JavaScript can be exercised directly by unit tests without rendering a WebView.
 */
export function generateOrbitalHtml(config: EncryptionConfig['orbital']): string {
  const getKeyScriptUrl = `${config?.baseUrl}/pie/v1/${config?.pieFormat}${config?.subscriberId}/getkey.js`;
  const encryptionScriptUrl = `${config?.baseUrl}/pie/v1/encryption.js`;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body>
      <script src="${getKeyScriptUrl}"></script>
      <script src="${encryptionScriptUrl}"></script>
      
      <script>
        (function notifyWhenReady() {
          var encryptionReady =
            typeof ProtectPANandCVV === 'function' && typeof ValidatePANChecksum === 'function';
          var pieReady = ${ORBITAL_PIE_READY_JS};

          if (encryptionReady && pieReady) {
            setTimeout(function() {
              window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'ENCRYPTION_READY' }));
            }, 0);
          } else {
            setTimeout(notifyWhenReady, 50);
          }
        })();

        async function validate(cardNumber) {
          if (typeof ValidatePANChecksum !== 'function') {
            throw new Error("ValidatePANChecksum function not available");
          }
          
          if (!ValidatePANChecksum(cardNumber)) {
            throw new Error("PAN has invalid checksum");
          }
        }

        async function encrypt(cardNumber, cvv, embed) {
          try {
            await validate(cardNumber);
            
            if (typeof ProtectPANandCVV !== 'function') {
              throw new Error("ProtectPANandCVV function not available");
            }
            
            const result = ProtectPANandCVV(cardNumber, cvv, !embed);
            
            if (!result || !Array.isArray(result) || result.length < 3) {
              throw new Error('Encryption failed - invalid result');
            }
            
            const [cryptCard, cryptCvv, integrityCheck] = result;
            
            if (${ORBITAL_PIE_MISSING_JS}) {
              throw new Error('PIE data not available');
            }
            
            window.ReactNativeWebView.postMessage(JSON.stringify({
              cryptCard,
              cryptCvv,
              integrityCheck,
              keyId: window.PIE.key_id,
              phase: window.PIE.phase,
              mode: embed ? 'eFPE' : 'FPE',
            })); 
          } catch (err) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              error: err.message || 'Unknown encryption error'
            }));
          }
        }
      </script>
    </body>
    </html>
  `;
}

export function buildOrbitalInjectScript(
  cardNumber: string,
  cvv: string,
  config: NonNullable<EncryptionConfig['orbital']>,
  pollMs: number
): string {
  return `
    (function waitAndEncrypt(remainingMs) {
      var encryptionReady =
        typeof ProtectPANandCVV === 'function' && typeof ValidatePANChecksum === 'function';
      var pieReady = ${ORBITAL_PIE_READY_JS};
      if (encryptionReady && pieReady) {
        encrypt("${cardNumber}", "${cvv}", ${config.mode === 'eFPE'});
      } else if (remainingMs > 0) {
        setTimeout(function() { waitAndEncrypt(remainingMs - 50); }, 50);
      } else {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          error: 'PIE encryption scripts not ready. Please check your connection and try again.'
        }));
      }
    })(${pollMs});
    true;
  `;
}
